import {
	Output,
	Mp4OutputFormat,
	WebMOutputFormat,
	CanvasSource,
	AudioBufferSource,
	getFirstEncodableVideoCodec,
} from 'mediabunny';
import {createMbTarget} from './targets';
import {writeMixedAudioTrack, type AudioExportItem} from './audio.mixdown';
import type {Composition} from '../composition/composition';
import {Timestamp} from '../models';
import {AudioClip} from '../clips/audio/audio';
import {VideoClip} from '../clips/video/video';
import {isScriptClip} from '../clips/script';
import { splitTimelineRangeBySpeakerMutes } from './script-speaker-mute';

type EncoderInit = {
	fps?: number;
	resolution?: number;
	videoBitrate?: number;
	audioBitrate?: number;
	debug?: boolean;
};

type RenderOptions = {
	signal?: AbortSignal;
	onProgress?: (progress: number, total: number, remainingMs: number) => void;
};

/**
 * Mediabunny-based video encoder for Composition.
 * Renders frames via the Pixi renderer.canvas and feeds them to CanvasSource.
 * Supports AudioClip, VideoClip, and ScriptLayerClip for audio extraction.
 */
export class MbEncoder {
	private composition: Composition;
	private fps: number;
	private resolution: number;
	private videoBitrate: number;
	private audioBitrate: number;
	private debug: boolean;

	public constructor(composition: Composition, init?: EncoderInit) {
		this.composition = composition;
		this.fps = init?.fps ?? 30;
		this.resolution = init?.resolution ?? 1;
		this.videoBitrate = init?.videoBitrate ?? 10e6;
		this.audioBitrate = init?.audioBitrate ?? 128e3;
		this.debug = init?.debug ?? false;
	}

	/**
	 * Collect all audio items from the composition for export
	 */
	private collectAudioItems(): AudioExportItem[] {
		const items: AudioExportItem[] = [];

		console.log(`[MbEncoder] Scanning ${this.composition.tracks.length} tracks for audio...`);

		for (const tr of this.composition.tracks) {
			console.log(`[MbEncoder] Track: id=${tr.id}, disabled=${tr.disabled}, clips=${tr.clips?.length ?? 0}`);
			
			for (const c of tr.clips as any[]) {
				if (!c) {
					console.log(`[MbEncoder]   - null clip, skipping`);
					continue;
				}
				if (tr.disabled) {
					console.log(`[MbEncoder]   - track disabled, skipping clip ${c.id}`);
					continue;
				}
				if (c.disabled) {
					console.log(`[MbEncoder]   - clip disabled: ${c.id}`);
					continue;
				}

				console.log(`[MbEncoder]   - Clip: id=${c.id}, type=${c.type}, constructor=${c.constructor?.name}`);

				// Handle AudioClip
				if (c instanceof AudioClip) {
					console.log(`[MbEncoder]     AudioClip detected, source=${!!c.source}, input=${!!c.source?.input}`);
					if (c.source?.input) {
						const segs: Array<{ srcStartSec: number; srcEndSec: number; compStartSec: number }> =
							Array.isArray((c as any).segments) ? (c as any).segments : [];
						if (Array.isArray(segs) && segs.length > 0) {
							for (const seg of segs) {
								const startSec = Number(seg?.compStartSec ?? NaN);
								const inSec = Number(seg?.srcStartSec ?? NaN);
								const outSec = Number(seg?.srcEndSec ?? NaN);
								if (!Number.isFinite(startSec) || !Number.isFinite(inSec) || !Number.isFinite(outSec) || outSec <= inSec) continue;
								items.push({
									startSec,
									inSec,
									outSec,
									getAudioTrack: async () => c.source.input.getPrimaryAudioTrack(),
									gain: Number(c.gain ?? 1.0) || 1.0,
									rate: Number((c as any).playbackRate ?? 1.0) || 1.0,
									kind: 'media',
									sourceKey: `audio:${c.id}`,
								});
							}
						} else {
							const startSec = c.start?.seconds ?? 0;
							const inSec = c.range?.[0]?.seconds ?? 0;
							const outSec = c.range?.[1]?.seconds ?? (inSec + (c.duration?.seconds ?? 0));
							console.log(`[MbEncoder]     Timing: start=${startSec}, in=${inSec}, out=${outSec}`);
							items.push({
								startSec,
								inSec,
								outSec,
								getAudioTrack: async () => c.source.input.getPrimaryAudioTrack(),
								gain: Number(c.gain ?? 1.0) || 1.0,
								rate: Number((c as any).playbackRate ?? 1.0) || 1.0,
								kind: 'media',
								sourceKey: `audio:${c.id}`,
							});
						}
					}
					continue;
				}

				// Handle VideoClip with embedded audio
				if (c instanceof VideoClip) {
					console.log(`[MbEncoder]     VideoClip detected, videoSource=${!!c.videoSource}, input=${!!c.videoSource?.input}`);
					if (c.videoSource?.input) {
						const segs: Array<{ srcStartSec: number; srcEndSec: number; compStartSec: number }> =
							Array.isArray((c as any).segments) ? (c as any).segments : [];
						if (Array.isArray(segs) && segs.length > 0) {
							for (const seg of segs) {
								const startSec = Number(seg?.compStartSec ?? NaN);
								const inSec = Number(seg?.srcStartSec ?? NaN);
								const outSec = Number(seg?.srcEndSec ?? NaN);
								if (!Number.isFinite(startSec) || !Number.isFinite(inSec) || !Number.isFinite(outSec) || outSec <= inSec) continue;
								items.push({
									startSec,
									inSec,
									outSec,
									getAudioTrack: async () => c.videoSource.input.getPrimaryAudioTrack(),
									gain: Number(c.gain ?? 1.0) || 1.0,
									rate: Number((c as any).playbackRate ?? 1.0) || 1.0,
									kind: 'media',
									sourceKey: `video:${c.id}`,
								});
							}
						} else {
							const startSec = c.start?.seconds ?? 0;
							const inSec = c.range?.[0]?.seconds ?? 0;
							const outSec = c.range?.[1]?.seconds ?? (inSec + (c.duration?.seconds ?? 0));
							console.log(`[MbEncoder]     Timing: start=${startSec}, in=${inSec}, out=${outSec}`);
							items.push({
								startSec,
								inSec,
								outSec,
								getAudioTrack: async () => c.videoSource.input.getPrimaryAudioTrack(),
								gain: Number(c.gain ?? 1.0) || 1.0,
								rate: Number((c as any).playbackRate ?? 1.0) || 1.0,
								kind: 'media',
								sourceKey: `video:${c.id}`,
							});
						}
					}
					continue;
				}

				// Handle ScriptLayerClip - can have multiple audio sources
				if (isScriptClip(c)) {
					console.log(`[MbEncoder]     ScriptLayerClip detected`);
					const audioEntries = c.getAudioEntries();
					const segments = (c as any).scriptSegments ?? [];
					console.log(`[MbEncoder]     Audio entries: ${audioEntries.length}, segments=${segments?.length ?? 0}`);

					if (Array.isArray(segments) && segments.length > 0 && Array.isArray(audioEntries) && audioEntries.length > 0) {
						const clipGain = Number((c as any).gain ?? 1.0) || 1.0;
						const clipRateRaw = Number((c as any).playbackRate);
						const clipRateOverride = Number.isFinite(clipRateRaw) && clipRateRaw > 0 ? clipRateRaw : undefined;
						type Placement = { offsetSec: number; inSec: number; outSec: number; rate: number; endOnClip: number };
						const EPS = 1e-3;

						for (let sourceIndex = 0; sourceIndex < audioEntries.length; sourceIndex++) {
							const entry: any = audioEntries[sourceIndex];
							if (!entry?.input) continue;
                            const speakerMuteRanges = Array.isArray((c as any)?.speakerMuteRanges)
                                ? ((c as any).speakerMuteRanges as any[])
                                : [];

							const entryGain = Number(entry.gain ?? 1.0) || 1.0;
							const baseGain = entryGain * clipGain;
							const placements: Placement[] = [];
							const entrySegs: any[] | undefined =
								Array.isArray(entry?.segments) && entry.segments.length > 0 ? entry.segments : undefined;

							if (entrySegs) {
								for (const s of entrySegs) {
									const offsetSec = Number(s?.offsetSec ?? NaN);
									const inSec = Number(s?.range?.inSec ?? NaN);
									const outSec = Number(s?.range?.outSec ?? NaN);
									const rate = Number(clipRateOverride ?? s?.playbackRate ?? entry?.playbackRate ?? 1.0) || 1.0;
									if (!Number.isFinite(offsetSec) || !Number.isFinite(inSec) || !Number.isFinite(outSec) || outSec <= inSec) continue;
									if (!Number.isFinite(rate) || rate <= 0) continue;
									const endOnClip = offsetSec + (outSec - inSec) / rate;
									placements.push({ offsetSec, inSec, outSec, rate, endOnClip });
								}
							} else {
								const offsetSec = Number(entry?.offsetSec ?? 0);
								const inSec = Number(entry?.range?.inSec ?? NaN);
								const outSec = Number(entry?.range?.outSec ?? NaN);
								const rate = Number(clipRateOverride ?? entry?.playbackRate ?? 1.0) || 1.0;
								if (Number.isFinite(offsetSec) && Number.isFinite(inSec) && Number.isFinite(outSec) && outSec > inSec && Number.isFinite(rate) && rate > 0) {
									const endOnClip = offsetSec + (outSec - inSec) / rate;
									placements.push({ offsetSec, inSec, outSec, rate, endOnClip });
								}
							}

							if (placements.length === 0) continue;

							for (const seg of segments as any[]) {
								const segStart = Number(seg?.srcStartSec ?? NaN);
								const segEnd = Number(seg?.srcEndSec ?? NaN);
								const segCompStart = Number(seg?.compStartSec ?? NaN);
								if (!Number.isFinite(segStart) || !Number.isFinite(segEnd) || segEnd <= segStart) continue;
								if (!Number.isFinite(segCompStart) || seg?.isGap) continue;

								let segSceneGainAdjust = 1.0;
								let segSourceGainMult = 1.0;
								try {
									const mid = (segStart + segEnd) / 2;
									const sceneIndex =
										typeof (c as any).findSceneIndexForTime === "function"
											? Number((c as any).findSceneIndexForTime(mid))
											: -1;
									if (Number.isFinite(sceneIndex) && sceneIndex >= 0) {
										const sceneProps =
											typeof (c as any).getSceneProperties === "function"
												? (c as any).getSceneProperties(sceneIndex)
												: (c as any).sceneProperties?.get?.(sceneIndex);
										const sceneGain = Number((sceneProps as any)?.gain);
										if (Number.isFinite(sceneGain) && clipGain > 0 && sceneGain !== clipGain) {
											segSceneGainAdjust = sceneGain / clipGain;
										}
										const audioSourceGains = (sceneProps as any)?.audioSourceGains;
										const multRaw =
											audioSourceGains && typeof audioSourceGains.get === "function"
												? Number(audioSourceGains.get(sourceIndex))
												: NaN;
										if (Number.isFinite(multRaw)) segSourceGainMult = multRaw;
									}
								} catch {}

								const overlaps: Array<{ start: number; end: number; p: Placement }> = [];
								for (const p of placements) {
									const o0 = Math.max(segStart, p.offsetSec);
									const o1 = Math.min(segEnd, p.endOnClip);
									if (o1 <= o0 + EPS) continue;
									overlaps.push({ start: o0, end: o1, p });
								}
								overlaps.sort((a, b) => a.start - b.start);

								for (const ov of overlaps) {
									const os = ov.start;
									const oe = ov.end;
									if (oe <= os + EPS) continue;

                                    const audiblePieces = splitTimelineRangeBySpeakerMutes({
                                        startSec: os,
                                        endSec: oe,
                                        sourceIndex,
                                        muteRanges: speakerMuteRanges,
                                    });
                                    if (audiblePieces.length === 0) continue;

									for (const piece of audiblePieces) {
										const ps = Number(piece.startSec ?? NaN);
										const pe = Number(piece.endSec ?? NaN);
										if (!Number.isFinite(ps) || !Number.isFinite(pe) || pe <= ps + EPS) continue;

										const timelineStart = segCompStart + (ps - segStart);
										const srcStart = ov.p.inSec + (ps - ov.p.offsetSec) * ov.p.rate;
										const srcEnd = ov.p.inSec + (pe - ov.p.offsetSec) * ov.p.rate;
										const gain = baseGain * segSceneGainAdjust * segSourceGainMult;
										items.push({
											startSec: timelineStart,
											inSec: srcStart,
											outSec: srcEnd,
											getAudioTrack: async () => entry.input.getPrimaryAudioTrack(),
											gain,
											kind: 'media',
											rate: ov.p.rate,
											sourceKey: `script:${c.id}:src${sourceIndex}`,
										});
									}
								}
							}
						}
						continue;
					}

					// Fallback: legacy, no script segments available.
                    const speakerMuteRanges = Array.isArray((c as any)?.speakerMuteRanges)
                        ? ((c as any).speakerMuteRanges as any[])
                        : [];
					for (let i = 0; i < audioEntries.length; i++) {
						const entry: any = audioEntries[i];
						if (!entry?.input) continue;

						const clipStartSec = c.start?.seconds ?? 0;
						const entryOffsetSec = Number(entry.offsetSec ?? 0);
						const startSec = clipStartSec + entryOffsetSec;
						const inSec = Number(entry.range?.inSec ?? 0);
						let outSec = entry.range?.outSec;
						const clipRateRaw = Number((c as any).playbackRate);
						const clipRateOverride = Number.isFinite(clipRateRaw) && clipRateRaw > 0 ? clipRateRaw : undefined;
						if (outSec === undefined) {
							const clipDur = c.duration?.seconds;
							outSec = clipDur ? (inSec + clipDur) : (inSec + 60);
						}
                        const rate = Number(clipRateOverride ?? entry.playbackRate ?? 1.0) || 1.0;
                        const clipLocalStart = entryOffsetSec;
                        const clipLocalEnd = clipLocalStart + Math.max(0, (outSec - inSec) / Math.max(0.001, rate));
                        const pieces = splitTimelineRangeBySpeakerMutes({
                            startSec: clipLocalStart,
                            endSec: clipLocalEnd,
                            sourceIndex: i,
                            muteRanges: speakerMuteRanges,
                        });
                        for (const piece of pieces) {
                            const pieceStartOffset = Number(piece.startSec ?? 0) - clipLocalStart;
                            const pieceEndOffset = Number(piece.endSec ?? 0) - clipLocalStart;
                            if (pieceEndOffset <= pieceStartOffset) continue;
                            items.push({
                                startSec: clipStartSec + Number(piece.startSec ?? 0),
                                inSec: inSec + pieceStartOffset * rate,
                                outSec: inSec + pieceEndOffset * rate,
                                getAudioTrack: async () => entry.input.getPrimaryAudioTrack(),
                                gain: Number(entry.gain ?? 1.0) || 1.0,
                                kind: 'media',
                                rate,
                                sourceKey: `script:${c.id}:src${i}`,
                            });
                        }
					}
					continue;
				}
				
				// Unknown clip type
				console.log(`[MbEncoder]     Unknown clip type, not AudioClip/VideoClip/ScriptLayerClip`);
			}
		}

		// Sort by timeline position
		items.sort((a, b) => a.startSec - b.startSec);
		
		console.log(`[MbEncoder] Total audio items collected: ${items.length}`);

		return items;
	}

	public async render(target: FileSystemFileHandle | string = 'video.mp4', opts?: RenderOptions): Promise<void> {
		if (!this.composition.renderer) {
			throw new Error('Renderer not initialized on composition');
		}

		const signal = opts?.signal;
		const onProgress = opts?.onProgress;

		// Prepare output
			const mbTarget = await createMbTarget(target);
			// Select container/codec
			const toEvenUp = (v: number): number => {
				const n = Math.max(2, Math.round(v));
				return n % 2 === 0 ? n : n + 1;
			};
			const width = toEvenUp(this.composition.settings.width * this.resolution);
			const height = toEvenUp(this.composition.settings.height * this.resolution);

		let format: Mp4OutputFormat | WebMOutputFormat = new Mp4OutputFormat();
		let codec: 'avc' | 'vp9' | 'av1' = 'avc';

		// Try MP4/AVC first
		const mp4 = new Mp4OutputFormat();
		const encodableAvc = await getFirstEncodableVideoCodec(
			mp4.getSupportedVideoCodecs(),
			{ width, height, bitrate: this.videoBitrate }
		);
		if (encodableAvc) {
			format = mp4;
			codec = 'avc';
		} else {
			// Fallback to WebM with VP9/AV1
			const webm = new WebMOutputFormat();
			const encodableWebm = await getFirstEncodableVideoCodec(
				webm.getSupportedVideoCodecs(),
				{ width, height, bitrate: this.videoBitrate }
			);
			if (!encodableWebm) {
				throw new Error('No encodable video codec found for MP4 or WebM');
			}
			format = webm;
			// Prefer vp9 if available, else av1 (encodableWebm contains specific codec string; map down)
			codec = (encodableWebm === 'vp8' || encodableWebm === 'vp9') ? 'vp9' : 'av1';
		}

			const output = new Output({ format, target: mbTarget.target });

			// Prepare video track (CanvasSource)
			const encoderConfig = {
				codec,
				bitrate: this.videoBitrate,
			};
			// IMPORTANT: never mutate the live editor renderer/canvas resolution for export.
			// We render into the composition's renderer canvas, then (optionally) downscale into a
			// dedicated export canvas with *even* dimensions (H.264/AVC requirement).
			const sourceCanvas = this.composition.renderer.canvas as HTMLCanvasElement;
			let encodeCanvas: HTMLCanvasElement = sourceCanvas;
			let encodeCtx: CanvasRenderingContext2D | null = null;
			if (sourceCanvas.width !== width || sourceCanvas.height !== height) {
				const c = document.createElement('canvas');
				c.width = width;
				c.height = height;
				const ctx = c.getContext('2d');
				if (!ctx) {
					throw new Error('Failed to create export canvas context');
				}
				ctx.imageSmoothingEnabled = true;
				// @ts-ignore
				if (typeof ctx.imageSmoothingQuality !== 'undefined') ctx.imageSmoothingQuality = 'high';
				encodeCanvas = c;
				encodeCtx = ctx;
			}
			const canvasSource = new CanvasSource(encodeCanvas, encoderConfig);
			output.addVideoTrack(canvasSource, { frameRate: this.fps });

		// Prepare audio track
		let audioSource: AudioBufferSource | null = null;
		try {
			const audioCodecForContainer = format instanceof Mp4OutputFormat ? 'aac' as const : 'opus' as const;
			audioSource = new AudioBufferSource({
				codec: audioCodecForContainer,
				bitrate: this.audioBitrate,
			});
			output.addAudioTrack(audioSource);
		} catch (e) {
			if (this.debug) {
				console.warn('[MbEncoder] Failed to create audio track:', e);
			}
			audioSource = null;
		}

			// Composition prep
			const originalFps = this.composition.fps;
			const originalState = this.composition.state;
			const originalFrame = this.composition.frame;
			const originalTimeSec = originalFps > 0 ? originalFrame / originalFps : 0;
			this.composition.fps = this.fps;
			this.composition.state = 'RENDER' as any;

			// Normalize export start without touching the visible canvas: reset frame + clip iterators.
			try {
				this.composition.frame = 0;
				for (const tr of this.composition.tracks) {
					for (const c of tr.clips as any[]) {
						if (typeof (c as any).onSeek === 'function') {
							(c as any).onSeek(0);
						}
					}
				}
			} catch {}

		const totalFrames = Math.floor(this.composition.duration.seconds * this.fps);
		const now = performance.now();
		const startMs = Date.now();
		const tracks = this.composition.tracks;
		const updatePromises: Promise<void>[] = [];
		let hasLoggedUpdateError = false;
		const toPixiClearColor = (hex: any): number => {
			const s = typeof hex === 'string' ? hex.trim() : '';
			if (s.startsWith('#') && s.length >= 7) {
				const n = parseInt(s.slice(1, 7), 16);
				return Number.isFinite(n) ? n : 0;
			}
			const n = Number(hex);
			return Number.isFinite(n) ? n : 0;
		};

			try {
				try {
					await output.start();

					// Write audio using overlap-aware mixdown.
					if (audioSource) {
						const items = this.collectAudioItems();
						console.log(`[MbEncoder] Processing ${items.length} audio items for export (mixed)`);
						await writeMixedAudioTrack({
							audioSource,
							items,
							durationSec: this.composition.duration.seconds,
							signal,
						});
					}

				// Render video frames
				for (let frame = 0; frame <= totalFrames; frame++) {
				if (signal?.aborted) {
					await output.cancel();
					throw new DOMException('User aborted rendering', 'AbortError');
				}

					// Advance composition and render to its canvas
					const ts = Timestamp.fromFrames(frame, this.fps);
					this.composition.frame = frame;
					// Prepare scene context + transitions (background color, fade overlay, cross-scene interpolation).
					// Export still manually updates tracks to await async decoders.
					let effectiveBackground = this.composition.settings.background as any;
					try {
						// @ts-ignore - present on Composition at runtime
						effectiveBackground = (this.composition as any).prepareFrameForRender?.(ts) ?? effectiveBackground;
					} catch {}
						updatePromises.length = 0;
						for (let i = 0; i < tracks.length; i++) {
							try {
								const maybe = tracks[i].update(ts);
								if (maybe && typeof (maybe as any).then === 'function') {
									updatePromises.push(maybe as Promise<void>);
								}
							} catch (e) {
								if (!hasLoggedUpdateError) {
									console.warn('[MbEncoder] Track update failed during export; continuing.', e);
									hasLoggedUpdateError = true;
								}
							}
						}
						if (updatePromises.length) {
							const results = await Promise.allSettled(updatePromises);
							if (!hasLoggedUpdateError) {
								const rejected = results.find((r): r is PromiseRejectedResult => r.status === 'rejected');
								if (rejected) {
									console.warn('[MbEncoder] Track update rejected during export; continuing.', rejected.reason);
									hasLoggedUpdateError = true;
								}
							}
						}
						this.composition.renderer.render({
							container: this.composition.stage,
							clear: true,
							clearColor: toPixiClearColor(effectiveBackground),
						});
						if (encodeCtx) {
							encodeCtx.clearRect(0, 0, width, height);
							encodeCtx.drawImage(sourceCanvas, 0, 0, width, height);
						}

					// Feed canvas to Mediabunny
					const t = frame / this.fps;
					const d = 1 / this.fps;
				await canvasSource.add(t, d);

				// Progress callback
				if (onProgress) {
					const elapsed = Date.now() - startMs;
					const done = Math.max(frame, 1);
					const remainingMs = Math.max(0, (elapsed / done) * (totalFrames - done));
					onProgress(done, totalFrames, remainingMs);
				}
			}

			if (this.debug) {
				const secs = (performance.now() - now) / 1000;
				const fps = totalFrames / Math.max(secs, 0.0001);
				console.info(`[MbEncoder] Rendered ~${fps.toFixed(2)} fps`);
			}

			await output.finalize();

					// Save/upload output if needed.
					const buf = (output.target as any).buffer as ArrayBuffer | null | undefined;
					const mime = format instanceof Mp4OutputFormat ? 'video/mp4' : 'video/webm';
					await (mbTarget as any).finalize?.({ buffer: buf ?? null }, mime);
				} catch (e) {
					// If we were streaming to OPFS, make sure we don't leave large temp files behind on failure/cancel.
					try {
						await (mbTarget as any).cleanup?.();
					} catch {}
					throw e;
				}
				} finally {
					// Reset composition state + restore visible frame/time.
					try {
						this.composition.state = originalState;
					this.composition.fps = originalFps;
					this.composition.frame = originalFrame;
					for (const tr of this.composition.tracks) {
						for (const c of tr.clips as any[]) {
							if (typeof (c as any).onSeek === 'function') {
								(c as any).onSeek(originalTimeSec);
							}
						}
					}
					this.composition.recomputeFrameAtCurrentTime?.();
				} catch {
					// Best-effort only.
				}
			}
		}
	}
